import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";

import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import { cc } from "afms/theme/commandCenterTheme";

// The server refuses to delete an accepted or paid quote, or one with an
// invoice behind it, and says which — that message is what the toast shows.
function DeleteQuotationModal({ open, onClose, onDeleted, quote }) {
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    if (!quote?.id || deleting) return;
    try {
      setDeleting(true);
      await AdminQuotationApiService.deleteQuotation(quote.id);
      toast.success("Quotation deleted");
      onDeleted?.();
      onClose?.();
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to delete quotation"
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={deleting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 6 }}>
        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: cc.ink }}>
          Delete Quote
        </SoftTypography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={deleting}
          sx={{ position: "absolute", right: 8, top: 8, color: cc.muted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: cc.page }}>
        <SoftTypography variant="body2" sx={{ color: cc.ink }}>
          Permanently delete quote #{quote?.id}
          {quote?.customerName ? ` for ${quote.customerName}` : ""}? This can&apos;t be undone.
        </SoftTypography>
        <SoftTypography variant="caption" color="text" display="block" mt={1}>
          A quote that&apos;s been accepted or paid, or has an invoice against it, can&apos;t be
          deleted.
        </SoftTypography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <SoftButton
          variant="outlined"
          color="secondary"
          size="small"
          onClick={onClose}
          disabled={deleting}
        >
          Cancel
        </SoftButton>
        <SoftButton
          variant="gradient"
          color="error"
          size="small"
          onClick={handleConfirm}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {deleting ? "Deleting…" : "Delete Quote"}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}

DeleteQuotationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDeleted: PropTypes.func,
  quote: PropTypes.object,
};

DeleteQuotationModal.defaultProps = { onDeleted: undefined, quote: null };

export default DeleteQuotationModal;
